/**
 * Countries eligible for flower delivery via Florist One.
 * Values are ISO 3166-1 alpha-2 codes matching the canonical DB format.
 */
export const FLOWER_DELIVERY_COUNTRIES = new Set(['US', 'CA']);

/**
 * Standard country list for the delivery address dropdown.
 * US and Canada are listed first since they are the only countries
 * supported for flower delivery via Florist One.
 *
 * Values are ISO 3166-1 alpha-2 codes; labels are the display names.
 * Uses Mantine v8 grouped ComboboxData format:
 *   { group: string, items: { value, label }[] }
 */
export const COUNTRY_OPTIONS = [
  {
    group: 'Flower Delivery Available',
    items: [
      { value: 'US', label: 'United States' },
      { value: 'CA', label: 'Canada' },
    ],
  },
  {
    group: 'International',
    items: [
      { value: 'AF', label: 'Afghanistan' },
      { value: 'AL', label: 'Albania' },
      { value: 'DZ', label: 'Algeria' },
      { value: 'AR', label: 'Argentina' },
      { value: 'AU', label: 'Australia' },
      { value: 'AT', label: 'Austria' },
      { value: 'BD', label: 'Bangladesh' },
      { value: 'BE', label: 'Belgium' },
      { value: 'BO', label: 'Bolivia' },
      { value: 'BR', label: 'Brazil' },
      { value: 'BG', label: 'Bulgaria' },
      { value: 'KH', label: 'Cambodia' },
      { value: 'CL', label: 'Chile' },
      { value: 'CN', label: 'China' },
      { value: 'CO', label: 'Colombia' },
      { value: 'CR', label: 'Costa Rica' },
      { value: 'HR', label: 'Croatia' },
      { value: 'CU', label: 'Cuba' },
      { value: 'CZ', label: 'Czech Republic' },
      { value: 'DK', label: 'Denmark' },
      { value: 'DO', label: 'Dominican Republic' },
      { value: 'EC', label: 'Ecuador' },
      { value: 'EG', label: 'Egypt' },
      { value: 'SV', label: 'El Salvador' },
      { value: 'EE', label: 'Estonia' },
      { value: 'ET', label: 'Ethiopia' },
      { value: 'FI', label: 'Finland' },
      { value: 'FR', label: 'France' },
      { value: 'DE', label: 'Germany' },
      { value: 'GH', label: 'Ghana' },
      { value: 'GR', label: 'Greece' },
      { value: 'GT', label: 'Guatemala' },
      { value: 'HN', label: 'Honduras' },
      { value: 'HK', label: 'Hong Kong' },
      { value: 'HU', label: 'Hungary' },
      { value: 'IS', label: 'Iceland' },
      { value: 'IN', label: 'India' },
      { value: 'ID', label: 'Indonesia' },
      { value: 'IR', label: 'Iran' },
      { value: 'IQ', label: 'Iraq' },
      { value: 'IE', label: 'Ireland' },
      { value: 'IL', label: 'Israel' },
      { value: 'IT', label: 'Italy' },
      { value: 'JM', label: 'Jamaica' },
      { value: 'JP', label: 'Japan' },
      { value: 'JO', label: 'Jordan' },
      { value: 'KE', label: 'Kenya' },
      { value: 'LV', label: 'Latvia' },
      { value: 'LB', label: 'Lebanon' },
      { value: 'LT', label: 'Lithuania' },
      { value: 'LU', label: 'Luxembourg' },
      { value: 'MY', label: 'Malaysia' },
      { value: 'MX', label: 'Mexico' },
      { value: 'MA', label: 'Morocco' },
      { value: 'MM', label: 'Myanmar' },
      { value: 'NP', label: 'Nepal' },
      { value: 'NL', label: 'Netherlands' },
      { value: 'NZ', label: 'New Zealand' },
      { value: 'NI', label: 'Nicaragua' },
      { value: 'NG', label: 'Nigeria' },
      { value: 'NO', label: 'Norway' },
      { value: 'PK', label: 'Pakistan' },
      { value: 'PA', label: 'Panama' },
      { value: 'PY', label: 'Paraguay' },
      { value: 'PE', label: 'Peru' },
      { value: 'PH', label: 'Philippines' },
      { value: 'PL', label: 'Poland' },
      { value: 'PT', label: 'Portugal' },
      { value: 'PR', label: 'Puerto Rico' },
      { value: 'RO', label: 'Romania' },
      { value: 'RU', label: 'Russia' },
      { value: 'SA', label: 'Saudi Arabia' },
      { value: 'RS', label: 'Serbia' },
      { value: 'SG', label: 'Singapore' },
      { value: 'SK', label: 'Slovakia' },
      { value: 'SI', label: 'Slovenia' },
      { value: 'ZA', label: 'South Africa' },
      { value: 'KR', label: 'South Korea' },
      { value: 'ES', label: 'Spain' },
      { value: 'LK', label: 'Sri Lanka' },
      { value: 'SE', label: 'Sweden' },
      { value: 'CH', label: 'Switzerland' },
      { value: 'TW', label: 'Taiwan' },
      { value: 'TZ', label: 'Tanzania' },
      { value: 'TH', label: 'Thailand' },
      { value: 'TT', label: 'Trinidad and Tobago' },
      { value: 'TN', label: 'Tunisia' },
      { value: 'TR', label: 'Turkey' },
      { value: 'UG', label: 'Uganda' },
      { value: 'UA', label: 'Ukraine' },
      { value: 'AE', label: 'United Arab Emirates' },
      { value: 'GB', label: 'United Kingdom' },
      { value: 'UY', label: 'Uruguay' },
      { value: 'VE', label: 'Venezuela' },
      { value: 'VN', label: 'Vietnam' },
      { value: 'ZW', label: 'Zimbabwe' },
    ],
  },
];

/**
 * O(1) lookup map from ISO code to display name, built from COUNTRY_OPTIONS.
 */
const COUNTRY_NAME_MAP: Map<string, string> = (() => {
  const map = new Map<string, string>();
  for (const group of COUNTRY_OPTIONS) {
    for (const item of group.items) {
      map.set(item.value, item.label);
    }
  }
  return map;
})();

/**
 * Reverse lookup map from lowercased country label to ISO code.
 * Used by CountrySelect to resolve browser-autofilled text
 * (e.g. "United States" -> "US") back to the value Mantine expects.
 */
const COUNTRY_LABEL_TO_CODE: Map<string, string> = (() => {
  const map = new Map<string, string>();
  for (const group of COUNTRY_OPTIONS) {
    for (const item of group.items) {
      map.set(item.label.toLowerCase(), item.value);
    }
  }
  return map;
})();

/**
 * Returns the display name for an ISO 3166-1 alpha-2 country code.
 * Falls back to the code itself if not recognized.
 */
export const getCountryName = (isoCode: string | null | undefined): string => {
  if (!isoCode) return '';
  return COUNTRY_NAME_MAP.get(isoCode) ?? isoCode;
};

/**
 * Resolves a country display name (case-insensitive) to its ISO 3166-1 alpha-2 code.
 * Returns null if the text doesn't match any known country label.
 *
 * Primary use case: browser autofill writes "United States" into a Mantine
 * `Select` search input — this function maps that back to "US".
 */
export const resolveCountryCode = (text: string): string | null => {
  if (!text) return null;
  return COUNTRY_LABEL_TO_CODE.get(text.trim().toLowerCase()) ?? null;
};
